import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt'
import DownloadIcon from '@mui/icons-material/Download'
import { Box, Button, Card, CardContent, CircularProgress, Stack, Tab, Tabs, Typography } from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import dayjs, { type Dayjs } from 'dayjs'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { useAppSelector } from '../store'
import { API_BASE_URL } from '../config/api'
import PageHeader from '../components/PageHeader'
import ReloadButton from '../components/ReloadButton'
import SamplingChooserView from './quality/SamplingChooserView'
import SamplingDataListView from './quality/SamplingDataListView'
import NestleSampleListView from './quality/NestleSampleListView'
import ComingSoonPage from './ComingSoonPage'
import { getUnbuild } from '../config/pageBuildStatus'
import { CARD_CONTENT_COMPACT, HIDE_ON_TABLET_INLINE } from '../theme/responsive'

const TAB_CONFIG = [
  // shortLabel hiển thị xs (< 600px) để 4 tab fit 1 dòng không scroll.
  // label full hiện sm+ (default).
  // reloadTags: tag (apiSlice) invalidate khi bấm Reload trên tab tương ứng,
  // để nút Reload áp dụng đúng cho từng sub-nav.
  { path: 'overview', label: 'Overview', shortLabel: 'Overview', reloadTags: ['Quality'] },
  { path: 'sample-data', label: 'Sample Data', shortLabel: 'Data', reloadTags: ['QualitySampling'] },
  { path: 'nestle-sample', label: 'Nestle Sample', shortLabel: 'Nestle', reloadTags: ['QualityNestleSample'] },
] as const

const headerButtonSx = {
  minHeight: { xs: 22, sm: 24 },
  px: { xs: 0.4, sm: 0.75 },
  py: 0.15,
  fontSize: { xs: '0.6rem', sm: '0.67rem' },
  lineHeight: 1.1,
  textTransform: 'none' as const,
  whiteSpace: 'nowrap' as const,
  minWidth: 0,
  '& .MuiButton-startIcon': {
    marginRight: { xs: '2px', sm: '6px' },
  },
}

// DatePicker filter — xs ép width 96px (shrink từ 130) để fit 1 dòng cùng
// 2 Export button + Reload trên 375px viewport.
const qualityDateSx = {
  width: { xs: 96, sm: 158 },
  '& .MuiInputBase-root': {
    minHeight: { xs: 22, sm: 26 },
    fontSize: { xs: '0.65rem', sm: '0.72rem' },
  },
  '& .MuiInputBase-input': {
    py: { xs: 0.1, sm: 0.2 },
    px: 0.5,
    fontSize: { xs: '0.65rem !important', sm: '0.72rem !important' },
  },
  '& .MuiInputLabel-root': {
    fontSize: { xs: '0.62rem', sm: '0.7rem' },
  },
}

/**
 * Access fiscal-year default for `module='quality_control'` (DocConvert_Quality_overview §4.1):
 *  - If current month >= Oct: start = Oct 1 current year, end = Sep 30 next year.
 *  - Else: start = Oct 1 previous year, end = Sep 30 current year.
 */
function fiscalYearDefault(now: Dayjs = dayjs()): { startDate: string; endDate: string } {
  const month = now.month() + 1
  const year = now.year()
  if (month >= 10) {
    return { startDate: `${year}-10-01`, endDate: `${year + 1}-09-30` }
  }
  return { startDate: `${year - 1}-10-01`, endDate: `${year}-09-30` }
}

const getDownloadFilename = (disposition: string | null): string | null => {
  if (!disposition) return null
  const filenameStar = /filename\*\s*=\s*([^;]+)/i.exec(disposition)
  if (filenameStar?.[1]) {
    const value = filenameStar[1].trim().replace(/^UTF-8''/i, '').replace(/^"|"$/g, '')
    try {
      return decodeURIComponent(value)
    } catch {
      return value
    }
  }
  const filename = /filename\s*=\s*([^;]+)/i.exec(disposition)
  if (!filename?.[1]) return null
  return filename[1].trim().replace(/^"|"$/g, '')
}

export default function QualityControlPage() {
  const location = useLocation()
  const navigate = useNavigate()

  const fyDefault = useMemo(() => fiscalYearDefault(), [])
  const [startDate, setStartDate] = useState(fyDefault.startDate)
  const [endDate, setEndDate] = useState(fyDefault.endDate)
  const [exporting, setExporting] = useState<'all' | 'filter' | null>(null)
  const email = useAppSelector((s) => s.auth.user?.email ?? '')
  const exportToken = 'JDHK-NDEB-DBCK-DGEJ'

  const activeIndex = useMemo(() => {
    const segment = location.pathname.split('/').pop() ?? ''
    const idx = TAB_CONFIG.findIndex((t) => t.path === segment)
    return idx >= 0 ? idx : 0
  }, [location.pathname])

  useEffect(() => {
    if (!location.pathname.startsWith('/quality')) return
    const segment = location.pathname.split('/').filter(Boolean)[1] ?? ''
    if (!TAB_CONFIG.some((t) => t.path === segment)) {
      navigate('/quality/overview', { replace: true })
    }
  }, [location.pathname, navigate])

  const hasInvalidRange = Boolean(startDate) && Boolean(endDate) && startDate > endDate

  const downloadQualityExcel = async (mode: 'all' | 'filter', url: string) => {
    if (!email) return
    setExporting(mode)
    try {
      const res = await fetch(url)
      if (!res.ok) throw new Error('Export failed')
      const blob = await res.blob()
      const href = URL.createObjectURL(blob)
      const a = document.createElement('a')
      const filename = getDownloadFilename(res.headers.get('content-disposition'))
      const downloadName =
        mode === 'filter' && filename ? filename.replace(/\.xlsx$/i, '_filter.xlsx') : filename
      a.href = href
      a.download = downloadName ?? (mode === 'filter' ? 'Quality_filter.xlsx' : 'Quality.xlsx')
      a.click()
      URL.revokeObjectURL(href)
    } finally {
      setExporting(null)
    }
  }

  const handleExportAll = async () => {
    const url = `${API_BASE_URL}/erp-legacy/download/quality/data?token=${encodeURIComponent(exportToken)}&email=${encodeURIComponent(email)}`
    await downloadQualityExcel('all', url)
  }

  const handleExportFilter = async () => {
    const url = `${API_BASE_URL}/erp-legacy/download/quality/data_filter?token=${encodeURIComponent(exportToken)}&email=${encodeURIComponent(email)}&start_date=${encodeURIComponent(startDate)}&end_date=${encodeURIComponent(endDate)}`
    await downloadQualityExcel('filter', url)
  }

  const current = TAB_CONFIG[activeIndex]
  const currentLabel = current.label

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <PageHeader
        title="Quality Control"
        module="Quality"
        actions={
          <Stack
            direction="row"
            spacing={{ xs: 0.25, sm: 0.45 }}
            alignItems="center"
            useFlexGap
            flexWrap={{ xs: 'nowrap', sm: 'wrap' }}
            justifyContent={{ xs: 'flex-start', lg: 'flex-end' }}
            sx={{ width: '100%', minWidth: 0 }}
          >
            {/* Date range filter — Pattern 11: label inside (shrink) + arrow icon giữa.
                xs ép width nhỏ qua qualityDateSx để cả Date + Reload + 2 Export fit 1 dòng
                trên 375px viewport (mục tiêu user: "1 dòng cho tôi"). */}
            <Stack
              direction="row"
              spacing={{ xs: 0.1, sm: 0.45 }}
              alignItems="center"
              useFlexGap
              flexWrap="nowrap"
              sx={{ minWidth: 0 }}
            >
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="Date Range"
                  format="DD-MMM-YYYY"
                  value={startDate ? dayjs(startDate) : null}
                  onChange={(d) => setStartDate(d ? d.format('YYYY-MM-DD') : '')}
                  slotProps={{
                    textField: {
                      size: 'small',
                      sx: qualityDateSx,
                      InputLabelProps: { shrink: true },
                      inputProps: { 'aria-label': 'Quality start date' },
                    },
                  }}
                />
                <ArrowRightAltIcon
                  fontSize="small"
                  sx={{
                    color: 'text.secondary',
                    fontSize: { xs: 12, sm: 18 },
                    mx: { xs: 0, sm: 0.25 },
                    flexShrink: 0,
                  }}
                  aria-hidden="true"
                />
                <DatePicker
                  format="DD-MMM-YYYY"
                  value={endDate ? dayjs(endDate) : null}
                  onChange={(d) => setEndDate(d ? d.format('YYYY-MM-DD') : '')}
                  slotProps={{
                    textField: {
                      size: 'small',
                      error: hasInvalidRange,
                      sx: qualityDateSx,
                      inputProps: { 'aria-label': 'Quality end date' },
                    },
                  }}
                />
              </LocalizationProvider>
            </Stack>

            <ReloadButton
              sx={headerButtonSx}
              apiTags={[...current.reloadTags]}
              baseTags={[]}
              label={<Box component="span" sx={HIDE_ON_TABLET_INLINE}>Reload</Box>}
            />
            {/* Export buttons — Pattern 10: 2 button cùng DownloadIcon khác parameter.
                Mobile/tablet (xs+sm): show chỉ discriminator "(Filter)"/"(All)";
                desktop (md+): full "Export (Filter)" / "Export (All)". */}
            <Button
              size="small"
              variant="outlined"
              sx={headerButtonSx}
              onClick={handleExportFilter}
              disabled={exporting !== null || !email || hasInvalidRange}
              title="Export (Filter)"
              startIcon={
                exporting === 'filter' ? (
                  <CircularProgress size={11} color="inherit" />
                ) : (
                  <DownloadIcon fontSize="small" />
                )
              }
            >
              <Box component="span" sx={HIDE_ON_TABLET_INLINE}>Export&nbsp;</Box>
              (Filter)
            </Button>
            <Button
              size="small"
              variant="outlined"
              sx={headerButtonSx}
              onClick={handleExportAll}
              disabled={exporting !== null || !email}
              title="Export (All)"
              startIcon={
                exporting === 'all' ? (
                  <CircularProgress size={11} color="inherit" />
                ) : (
                  <DownloadIcon fontSize="small" />
                )
              }
            >
              <Box component="span" sx={HIDE_ON_TABLET_INLINE}>Export&nbsp;</Box>
              (All)
            </Button>
          </Stack>
        }
      />
      {hasInvalidRange && (
        <Typography variant="caption" color="error" sx={{ alignSelf: 'flex-end', mb: 1 }}>
          Start date must be before or equal to end date
        </Typography>
      )}
      <Card sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <Tabs
          value={activeIndex}
          onChange={(_, value: number) => {
            navigate(`/quality/${TAB_CONFIG[value].path}`, { replace: false })
          }}
          // xs: 4 tab fit 1 dòng (~178px tổng) → KHÔNG cần scrollable.
          // sm+: scrollable phòng overflow.
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            minHeight: { xs: 30, sm: 34 },
            '& .MuiTabs-indicator': {
              height: 2.5,
              borderRadius: '3px 3px 0 0',
            },
            '& .MuiTab-root': {
              position: 'relative',
              minHeight: { xs: 30, sm: 34 },
              minWidth: 0,
              py: 0.25,
              px: { xs: 0.75, sm: 1.5 },
              fontSize: { xs: '0.68rem', sm: '0.78rem' },
              fontWeight: 600,
              letterSpacing: 0.15,
              textTransform: 'none',
              color: 'text.secondary',
              borderRadius: '6px 6px 0 0',
              transition: 'color 140ms ease, background-color 140ms ease',
              '&:hover': {
                color: 'text.primary',
                bgcolor: 'action.hover',
              },
              '&.Mui-selected': {
                color: 'primary.main',
                fontWeight: 700,
              },
              '&:not(:last-of-type)::after': {
                content: '""',
                position: 'absolute',
                right: 0,
                top: '50%',
                transform: 'translateY(-50%)',
                height: 16,
                width: '1px',
                bgcolor: 'divider',
                opacity: 0.6,
                transition: 'opacity 140ms ease',
              },
              '&:hover::after, &.Mui-selected::after': { opacity: 0 },
            },
            // Ẩn scroll arrow xs (đã đủ chỗ 4 tab); giữ sm+ phòng future tab dài.
            '& .MuiTabs-scrollButtons': {
              display: { xs: 'none', sm: 'inline-flex' },
            },
          }}
        >
          {TAB_CONFIG.map((tab) => (
            <Tab
              key={tab.path}
              label={
                <>
                  <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
                    {tab.shortLabel}
                  </Box>
                  <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                    {tab.label}
                  </Box>
                </>
              }
            />
          ))}
        </Tabs>
        <CardContent sx={{ ...CARD_CONTENT_COMPACT, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          {/*
            Quality tabs: Sampling (chooser), Sampling Data (PSS Label print is
            merged into the row "Print" button there), and Nestle Sample (export).
          */}
          {(() => {
            const unbuild = getUnbuild(`/quality/${current.path}`)
            if (unbuild) {
              return <ComingSoonPage title={currentLabel} embedded status={unbuild.status} />
            }
            return current.path === 'overview' ? (
              <SamplingChooserView />
            ) : current.path === 'nestle-sample' ? (
              <NestleSampleListView startDate={startDate} endDate={endDate} />
            ) : current.path === 'sample-data' ? (
              <SamplingDataListView dateFrom={startDate} dateTo={endDate} />
            ) : (
              <ComingSoonPage title={currentLabel} embedded />
            )
          })()}
        </CardContent>
      </Card>
    </Box>
  )
}
